import React from "react";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/pages/home/components/Header";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

interface FAQCategory {
  id: string;
  title: string;
  icon: string;
  faqs: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    id: "the-fund-and-how-it-works",
    title: "La Caisse et son fonctionnement",
    icon: "ri-building-4-line",
    faqs: [
      {
        question: "Quel type de régime est la Caisse de retraite des musiciens du Canada?",
        answer: (
          <p>Le régime est un régime de retraite interentreprises à prestations déterminées, communément appelé « régime à prestations cibles ». Il s'agit d'un régime dans lequel plusieurs employeurs cotisent à la Caisse pour verser des prestations mensuelles déterminées aux membres du régime lorsqu'ils prennent leur retraite. Les prestations sont prédéterminées selon une formule basée sur les cotisations des employeurs. Les taux d'accumulation ou les prestations accumulées peuvent être ajustés en fonction de l'état de capitalisation du régime.</p>
        ),
      },
      {
        question: "Comment puis-je adhérer au régime?",
        answer: (
          <p>Vous êtes admissible à devenir membre du régime si vous travaillez pour un employeur qui a conclu une convention collective avec la Fédération canadienne des musiciens (FCM), ou avec l'une de ses sections locales affiliées. Vous êtes également admissible si vous travaillez pour un employeur qui a conclu une entente de participation avec les fiduciaires et qui a accepté de cotiser à la Caisse en votre nom.</p>
        ),
      },
      {
        question: "Quel type de travail dois-je effectuer pour adhérer au régime?",
        answer: (
          <div>
            <p className="mb-3">Vous pouvez travailler comme :</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Musicien, y compris compositeur, arrangeur, copiste, réviseur, bibliothécaire, instrumentaliste, chef ou entrepreneur; ou</li>
              <li>Dirigeant ou représentant élu ou nommé de la FCM ou de l'une de ses sections locales affiliées; ou</li>
              <li>Dirigeant ou employé de bureau de la Caisse, de Musicians' Rights Organization Canada (MROC), ou de la FCM ou de l'une de ses sections locales affiliées.</li>
            </ul>
          </div>
        ),
      },
      {
        question: "Que signifie « FCM »?",
        answer: (
          <p>« FCM » désigne la Fédération canadienne des musiciens ou l'American Federation of Musicians of the United States and Canada, y compris leurs sections locales canadiennes affiliées.</p>
        ),
      },
      {
        question: "Comment puis-je devenir membre du régime?",
        answer: (
          <div>
            <p className="mb-3">Vous pouvez devenir membre du régime de trois façons :</p>
            <ol className="list-decimal pl-5 space-y-3">
              <li>Vous avez 24 mois de service d'acquisition sans période de six mois civils sans cotisations. C'est ainsi que la plupart des musiciens deviennent membres; ou</li>
              <li>Si votre première cotisation est versée en votre nom après le 31 décembre 2000, vous deviendrez membre du régime le premier jour de l'année civile suivant l'année civile au cours de laquelle vous avez 700 heures d'<a target="_blank" href="/fr/faq?category=the-fund-and-how-it-works&question=5" className="text-[#1a8fc1] underline hover:text-[#0d8ab5] transition-colors">emploi visé</a> ou une <a target="_blank" href="/fr/faq?category=the-fund-and-how-it-works&question=5" className="text-[#1a8fc1] underline hover:text-[#0d8ab5] transition-colors">rémunération visée</a> égale ou supérieure à 35 % du <a target="_blank" href="/fr/faq?category=the-fund-and-how-it-works&question=5" className="text-[#1a8fc1] underline hover:text-[#0d8ab5] transition-colors">maximum des gains annuels ouvrant droit à pension</a>; ou</li>
              <li>Si la première cotisation versée en votre nom a été effectuée avant le 1er janvier 2001, la date à laquelle vous devenez membre du régime dépend de plusieurs facteurs. Ceux-ci comprennent la date à laquelle les premières cotisations au régime ont été versées pour vous et si votre travail a été effectué en tant que musicien ou non. Veuillez communiquer avec le Bureau de la Caisse pour plus de renseignements.</li>
            </ol>
          </div>
        ),
      },
      {
        question: "Que signifient « emploi visé », « rémunération visée » et « maximum des gains annuels ouvrant droit à pension »?",
        answer: (
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>L'emploi visé</strong> est le travail effectué pour un employeur tenu de cotiser au régime.</li>
            <li><strong>La rémunération visée</strong> correspond aux salaires de la grille sur lesquels des cotisations de retraite ont été versées à la Caisse en votre nom.</li>
            <li><strong>Le maximum des gains annuels ouvrant droit à pension</strong> est un terme défini dans le Régime de pensions du Canada pour les personnes assujetties à la législation sur les pensions du Québec ou au Régime de rentes du Québec. Le montant pour 2023 est de 66 600 $. Ce montant peut changer chaque année.</li>
          </ul>
        ),
      },
      {
        question: "Quand ai-je droit à une prestation de retraite?",
        answer: (
          <div>
            <p className="mb-3">Vous avez droit à une prestation de retraite une fois que vous avez acquis des droits à votre prestation. L'acquisition des droits signifie que vous avez droit à votre prestation. Vous pouvez acquérir des droits selon l'une des règles suivantes :</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Vous étiez un participant qui devient membre du régime après le 31 décembre 2000; ou</li>
              <li>Vous étiez déjà membre du régime le 31 décembre 2000 et des cotisations sont versées en votre nom après le 31 décembre 2000, mais avant la fin de votre adhésion au régime; ou</li>
              <li>Si vous êtes devenu membre du régime avant le 1er janvier 2001 et qu'aucune cotisation d'employeur n'est versée en votre nom après le 31 décembre 2000, auquel cas vous acquérez des droits à la première des dates suivantes : lorsque vous avez accumulé au moins 24 mois de service d'acquisition ou lorsque vous atteignez l'âge de 65 ans alors que vous êtes membre du régime.</li>
            </ul>
          </div>
        ),
      },
      {
        question: "Quelles sont les définitions clés du régime?",
        answer: (
          <div>
            <p className="mb-3">Cliquez ci-dessous pour vous aider à comprendre les définitions clés du régime.</p>
            <a
              href="/wp-content/uploads/2020/10/MPFC_Definitions_FR_FINAL.pdf"
              target="_blank"
              rel="nofollow"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              Voir les définitions clés du régime
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        ),
      },
    ],
  },
  {
    id: "pension-benefits",
    title: "Prestations de retraite",
    icon: "ri-hand-coin-line",
    faqs: [
      {
        question: "Quels types de prestations le régime verse-t-il?",
        answer: (
          <div>
            <p className="mb-3">Vous pouvez recevoir l'un des types de prestations suivants du régime :</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Retraite normale, à compter de 65 ans</li>
              <li>Retraite anticipée, à compter de 55 ans mais avant 65 ans</li>
              <li>Retraite spéciale</li>
              <li>Prestation d'invalidité</li>
            </ul>
          </div>
        ),
      },
      {
        question: "Ma pension est-elle indexée après la retraite?",
        answer: (
          <div>
            <p className="mb-3">Une « pension indexée » est une pension qui est augmentée périodiquement pour tenir compte des augmentations de l'indice des prix à la consommation (IPC). L'indice des prix à la consommation (IPC) est une mesure du degré de variation du prix des biens et services achetés par les consommateurs canadiens et est déterminé mensuellement par Statistique Canada.</p>
            <p className="mb-3">Seulement environ 32 % de tous les régimes à prestations déterminées au Canada offrent une forme quelconque d'indexation garantie après la retraite. La plupart d'entre eux sont des régimes du secteur public dont le financement provient, du moins en partie, des contribuables.</p>
            <p>Le conseil de fiduciaires examine régulièrement les différents types de prestations offertes par le régime et apporte des modifications périodiquement en fonction de ce qu'il estime être dans le meilleur intérêt des membres. Étant donné que très peu, voire aucun, régime de retraite interentreprises comme le nôtre n'offre d'indexation garantie, et puisque le coût est important, les fiduciaires n'ont pas mis en œuvre l'indexation garantie dans le cadre des dispositions de prestations du régime.</p>
          </div>
        ),
      },
      {
        question: "Qu'est-ce qu'une retraite spéciale?",
        answer: (
          <div>
            <p className="mb-3">Vous pouvez prendre une retraite spéciale si vous :</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Avez au moins 55 ans et n'avez pas encore 65 ans</li>
              <li>Avez acquis des droits</li>
              <li>Êtes un membre actif à 55 ans ou plus</li>
              <li>Avez au moins 15 années avec droits acquis pour les cotisations de la partie 1 et au moins 20 années avec droits acquis pour les cotisations de la partie 2, années au cours desquelles des cotisations ont été versées chaque année en votre nom</li>
              <li>Les 15 ou 20 années n'incluent pas les périodes qui ont été annulées avant l'acquisition de vos droits</li>
            </ul>
            <p className="mt-3">La retraite spéciale ne s'applique qu'au travail effectué avant le 1er janvier 2013. Vous devez également être actif à la date d'entrée en vigueur de la retraite, ce qui signifie que vous avez des cotisations pour un engagement dans la période de 24 mois précédant la date d'entrée en vigueur de la retraite.</p>
          </div>
        ),
      },
      {
        question: "Qu'est-ce qu'une prestation d'invalidité?",
        answer: (
          <div>
            <p className="mb-3">Vous pourriez être admissible à une prestation d'invalidité si vous :</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Devenez totalement et définitivement invalide alors que vous êtes un membre actif du régime</li>
              <li>N'êtes pas admissible à une retraite normale</li>
              <li>Avez acquis des droits au moment de votre invalidité</li>
              <li>N'êtes pas admissible à recevoir des prestations d'invalidité de votre employeur ou en vertu d'un programme d'assurance offert par votre employeur</li>
            </ul>
          </div>
        ),
      },
      {
        question: "Quel sera le montant de ma prestation?",
        answer: (
          <div>
            <p className="mb-3">Le montant de votre prestation de retraite mensuelle dépendra :</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Des cotisations versées par votre ou vos employeurs pour votre emploi visé</li>
              <li>Du type de retraite que vous recevez : normale, anticipée, spéciale ou d'invalidité</li>
              <li>De la forme de paiement de la retraite que vous choisissez, qui est basée en partie sur le fait que vous ayez ou non un conjoint à la date du début du versement de votre retraite</li>
            </ul>
          </div>
        ),
      },
      {
        question: "Comment ma prestation est-elle calculée?",
        answer: (
          <div>
            <p className="mb-3">Votre retraite est calculée en trois parties :</p>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li><strong>Partie 1</strong> est votre prestation de retraite liée aux cotisations pour les engagements ayant eu lieu jusqu'au 31 décembre 2010 inclusivement.</li>
              <li><strong>Partie 2</strong> est votre prestation de retraite liée aux cotisations pour les engagements ayant eu lieu du 1er janvier 2011 au 31 décembre 2012 inclusivement.</li>
              <li><strong>Partie 3</strong> est votre prestation de retraite liée aux cotisations pour les engagements ayant eu lieu à compter du 1er janvier 2013.</li>
            </ul>
            <p className="mb-3">Par exemple, voici comment une retraite normale est calculée :</p>
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2.5 pr-3 text-[#0d1f4c] font-semibold text-xs">Type et période de cotisation</th>
                    <th className="text-right py-2.5 pl-3 text-[#0d1f4c] font-semibold text-xs whitespace-nowrap">Pension mensuelle par tranche de 100 $ de cotisations</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="bg-[#f4f8fc]/60">
                    <td className="py-2.5 pr-3 font-semibold text-[#0d1f4c] text-xs">PARTIE 1</td>
                    <td className="py-2.5 pl-3 text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Cotisations pour emploi visé avant 1992</td>
                    <td className="py-2.5 pl-3 text-right font-medium">3,80 $</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Cotisations pour emploi visé du 1er janvier 1992 au 30 avril 2006 inclusivement</td>
                    <td className="py-2.5 pl-3 text-right font-medium">3,70 $</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Cotisations pour emploi visé du 1er mai 2006 au 31 décembre 2010 inclusivement</td>
                    <td className="py-2.5 pl-3 text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3 pl-4 text-gray-500">Cotisations jusqu'à 10 % des salaires de la grille</td>
                    <td className="py-2.5 pl-3 text-right font-medium">3,70 $</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3 pl-4 text-gray-500">Cotisations entre 10 % et 12 % des salaires de la grille</td>
                    <td className="py-2.5 pl-3 text-right font-medium">2,00 $</td>
                  </tr>
                  <tr className="bg-[#f4f8fc]/60">
                    <td className="py-2.5 pr-3 font-semibold text-[#0d1f4c] text-xs">PARTIE 2</td>
                    <td className="py-2.5 pl-3 text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Cotisations pour emploi visé du 1er janvier 2011 au 31 décembre 2012 inclusivement</td>
                    <td className="py-2.5 pl-3 text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3 pl-4 text-gray-500">Cotisations jusqu'à 10 % des salaires de la grille</td>
                    <td className="py-2.5 pl-3 text-right font-medium">3,25 $</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3 pl-4 text-gray-500">Cotisations entre 10 % et 12 % des salaires de la grille</td>
                    <td className="py-2.5 pl-3 text-right font-medium">2,00 $</td>
                  </tr>
                  <tr className="bg-[#f4f8fc]/60">
                    <td className="py-2.5 pr-3 font-semibold text-[#0d1f4c] text-xs">PARTIE 3</td>
                    <td className="py-2.5 pl-3 text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Toutes les cotisations pour emploi visé à compter du 1er janvier 2013, maximum 18 %</td>
                    <td className="py-2.5 pl-3 text-right font-medium">3,25 $</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 mb-2 font-medium">Les cotisations pour cette période sont divisées en deux montants égaux appliqués comme suit :</p>
            <ul className="list-disc pl-5 space-y-1 mb-3">
              <li>50 % est affecté comme cotisations ordinaires servant au calcul de vos prestations.</li>
              <li>50 % est affecté comme cotisations de soutien qui aideront à couvrir le coût réel de vos prestations et ne seront pas utilisées pour le calcul des prestations.</li>
            </ul>
            <p className="mb-3">Il s'agit effectivement d'un taux d'accumulation de 1,625 sur l'ensemble des cotisations versées en votre nom.</p>
            <p className="mb-4">Pour un exemple de calcul de retraite normale et pour plus de renseignements sur le calcul d'autres types de retraite du régime, veuillez consulter le résumé du régime.</p>
            <Link
              to="/fr#summary-plan"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              Voir le résumé du régime
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>
        ),
      },
      {
        question: "Qui peut être mon bénéficiaire?",
        answer: (
          <p>Votre bénéficiaire est la ou les personnes que vous désignez pour recevoir les prestations du régime si vous décédez. Si vous avez un conjoint à la date du début de votre retraite, il ou elle est automatiquement votre bénéficiaire, à moins que vous et votre conjoint ne renonciez aux droits de votre conjoint à une prestation. Si un membre est assujetti aux lois sur les pensions du Québec et désigne son conjoint comme bénéficiaire, cette désignation est irrévocable, sauf indication contraire dans la désignation.</p>
        ),
      },
      {
        question: "Comment puis-je changer mon bénéficiaire?",
        answer: (
          <div>
            <p className="mb-3">Vous pouvez changer votre bénéficiaire en tout temps en avisant le Bureau de la Caisse par écrit. Pour que le changement soit effectif, le Bureau de la Caisse doit le recevoir avant que tout paiement ne soit effectué à votre bénéficiaire précédemment désigné.</p>
            <p>Si vous avez un conjoint au moment de votre retraite, il ou elle a le droit de recevoir une prestation de survivant si vous décédez et est automatiquement désigné comme votre bénéficiaire, à moins que vous et votre conjoint ne renonciez à ce droit par écrit.</p>
          </div>
        ),
      },
      {
        question: "Ma pension est-elle garantie?",
        answer: (
          <div>
            <p className="mb-3">Non, votre pension n'est pas garantie. Les prestations offertes par ce régime ne sont pas garanties par le Fonds de garantie des prestations de retraite du gouvernement de l'Ontario. Le Fonds de garantie des prestations de retraite de l'Ontario ne s'applique pas aux régimes de retraite interentreprises.</p>
            <p>Dans le cas improbable où le régime prendrait fin et ne serait pas entièrement capitalisé, votre prestation de retraite pourrait faire l'objet d'une réduction. Comme il s'agit d'un régime à prestations cibles, les prestations de retraite peuvent être réduites.</p>
          </div>
        ),
      },
      {
        question: "Mes versements de retraite augmenteront-ils en fonction de l'inflation?",
        answer: (
          <div>
            <p className="mb-3">Non. Vos versements de retraite n'augmenteront pas en fonction de l'inflation. Cette augmentation est souvent appelée « indexation ».</p>
            <p>Étant donné que très peu, voire aucun, régime de retraite interentreprises comme le nôtre n'offre d'indexation garantie, et puisque le coût de l'indexation est important, les fiduciaires n'ont pas mis en œuvre l'indexation garantie dans le cadre du régime.</p>
          </div>
        ),
      },
      {
        question: "Puis-je prendre ma retraite avant 65 ans?",
        answer: (
          <div>
            <p className="mb-3">Vous pouvez prendre votre retraite dès l'âge de 55 ans et recevoir une pension du régime si vous avez acquis des droits à votre prestation au moment de votre retraite.</p>
            <p className="mb-3">Votre retraite sera calculée de la même manière qu'une retraite normale. Cependant, puisque vous recevrez votre retraite pendant une plus longue période que si vous preniez votre retraite à 65 ans, votre versement mensuel de retraite sera réduit. La réduction est basée sur votre âge au moment de la retraite. Plus vous êtes jeune au moment de la retraite, plus la réduction de votre versement mensuel de retraite sera importante.</p>
            <p className="mb-4">Veuillez consulter le résumé du régime pour plus de renseignements sur la retraite anticipée.</p>
            <Link
              to="/fr#summary-plan"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              Voir le résumé du régime
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>
        ),
      },
      {
        question: "Qu'arrive-t-il à ma prestation si je prends ma retraite après 65 ans?",
        answer: (
          <p>Si vous prenez votre retraite après 65 ans, votre prestation continuera de s'accumuler jusqu'à ce que vous commenciez à recevoir votre retraite. Vous devez commencer votre retraite au plus tard le 1er décembre de l'année où vous atteignez 71 ans.</p>
        ),
      },
      {
        question: "Puis-je continuer à accumuler des prestations après 71 ans?",
        answer: (
          <p>Non. Conformément à la Loi de l'impôt sur le revenu, personne ne peut accumuler de prestations après le 1er décembre de l'année où il atteint l'âge de 71 ans.</p>
        ),
      },
      {
        question: "Qu'arrive-t-il à ma prestation si je deviens invalide?",
        answer: (
          <p>Si vous avez acquis des droits à votre prestation et que vous devenez totalement et définitivement invalide, et que vous n'êtes pas admissible à recevoir des prestations d'invalidité de votre employeur ou d'un programme d'assurance offert par votre employeur, vous pourriez avoir droit à une prestation d'invalidité de la Caisse de retraite.</p>
        ),
      },
      {
        question: "Comment les fiduciaires savent-ils que le régime dispose de suffisamment d'argent pour payer les prestations aux retraités et bénéficiaires actuels et futurs?",
        answer: (
          <div>
            <p className="mb-3">Tous les trois ans, conformément à la législation sur les pensions, la Caisse fait appel aux services d'actuaires professionnels pour évaluer le régime et confirmer qu'il dispose de suffisamment d'argent pour payer les prestations aux retraités et bénéficiaires actuels et futurs. C'est ce qu'on appelle une évaluation actuarielle.</p>
            <p>Une évaluation actuarielle peut être effectuée plus fréquemment que tous les trois ans, à la discrétion des fiduciaires.</p>
          </div>
        ),
      },
      {
        question: "Qu'est-ce que la prestation temporaire/de raccordement offerte aux membres du Québec qui prennent une retraite anticipée?",
        answer: (
          <div>
            <p className="mb-3">Un membre du Québec qui choisit une retraite anticipée a la possibilité de prendre une prestation temporaire. Une prestation temporaire fournira au membre un revenu supplémentaire jusqu'à l'âge de 65 ans, ou avant s'il le souhaite.</p>
            <p className="mb-3">Le montant annuel d'une prestation temporaire est choisi par le membre avant que tout versement de retraite ne soit effectué. Le montant choisi ne peut pas dépasser 40 % du maximum des gains admissibles en vertu du Régime de rentes du Québec pour l'année au cours de laquelle le versement de la retraite commence.</p>
            <p>Le fait de prendre une prestation temporaire entraîne des versements de retraite plus élevés pendant la période temporaire que ce qui aurait été versé autrement. Par conséquent, la pension viagère versée à partir de la retraite est ajustée actuariellement pour tenir compte du versement de la prestation temporaire.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: "death-benefits",
    title: "Prestations de décès",
    icon: "ri-shield-user-line",
    faqs: [
      {
        question: "Qu'arrive-t-il à ma prestation de retraite si je décède avant ma retraite?",
        answer: (
          <div>
            <p className="mb-3">Si vous n'avez pas acquis de droits à votre prestation de retraite et que vous décédez avant votre retraite, aucune prestation n'est payable à votre bénéficiaire.</p>
            <p className="mb-3">Si vous avez acquis des droits à votre prestation de retraite et que vous décédez avant votre retraite, votre bénéficiaire recevra une prestation de décès avant la retraite du régime.</p>
            <p className="mb-4">Veuillez consulter le résumé du régime pour plus de renseignements sur la prestation de décès avant la retraite.</p>
            <Link
              to="/fr#summary-plan"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              Voir le résumé du régime
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>
        ),
      },
      {
        question: "Qu'arrive-t-il à ma prestation de retraite si je décède après avoir commencé à recevoir ma prestation?",
        answer: (
          <div>
            <p className="mb-3">Si vous décédez après avoir commencé à recevoir votre prestation de retraite, votre bénéficiaire pourrait recevoir une prestation de retraite, selon le type de prestation que vous avez choisi au moment de la retraite.</p>
            <p className="mb-4">Veuillez consulter le résumé du régime pour plus de renseignements, ou communiquer avec le Bureau de la Caisse par téléphone au 1-888-462-6666.</p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/fr#summary-plan"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
              >
                Voir le résumé du régime
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
              <a
                href="mailto:info@mpfcanada.ca"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
              >
                Envoyer un courriel au Bureau de la Caisse
                <i className="ri-mail-line text-sm"></i>
              </a>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: "contribution-information",
    title: "Renseignements sur les cotisations",
    icon: "ri-bank-line",
    faqs: [
      {
        question: "Comment les cotisations de retraite sont-elles versées à la Caisse?",
        answer: (
          <p>Les cotisations de retraite sont versées en votre nom par vos employeurs. Les cotisations versées en votre nom sont basées sur vos dates d'emploi et vos salaires de la grille ou, si vous êtes un dirigeant ou employé de section locale, sur votre rémunération. Les cotisations versées à la Caisse sont détenues dans un fonds en fiducie pour fournir des prestations aux membres admissibles et à leurs bénéficiaires, et pour payer les dépenses administratives de la Caisse.</p>
        ),
      },
      {
        question: "Puis-je verser mes propres cotisations de retraite?",
        answer: (
          <p>Non. Les cotisations des employés ne sont ni requises ni permises. Pour que le régime soit considéré comme un régime de retraite agréé, la Loi de l'impôt sur le revenu exige qu'il existe une véritable relation employeur-employé pour chaque membre qui participe au régime.</p>
        ),
      },
      {
        question: "Des cotisations de retraite peuvent-elles être versées sur des cachets négociés?",
        answer: (
          <p>Non. Les cotisations de retraite ne peuvent pas être versées sur des cachets négociés. Les cotisations ne peuvent être versées qu'en vertu d'une convention collective ou de contrats d'engagement locaux, qui sont basés sur les salaires de la grille de la FCM/AFM.</p>
        ),
      },
      {
        question: "Puis-je faire verser des cotisations de retraite en mon nom dépassant 18 % de la grille?",
        answer: (
          <p>Non. Le plafond de 18 % est conforme à la règle de la Loi de l'impôt sur le revenu qui limite les cotisations totales à un régime de retraite agréé à 18 % de la rémunération. Gardez à l'esprit que, bien que les cotisations jusqu'à 18 % de la grille soient permises, le taux de cotisation de votre employeur est limité au taux spécifié dans la convention collective ou le tarif des cachets de la section locale.</p>
        ),
      },
      {
        question: "Si je travaille à l'extérieur du Canada, est-ce que je continue à recevoir des cotisations de retraite?",
        answer: (
          <div>
            <p className="mb-3">Si vous travaillez temporairement à l'extérieur du Canada pour un employeur canadien, votre employeur peut continuer à verser des cotisations de retraite au régime. Notez que la Loi de l'impôt sur le revenu limite la période pendant laquelle vous pouvez recevoir des cotisations de retraite au régime lorsque vous travaillez aux États-Unis.</p>
            <p className="mb-3">Si vous travaillez aux États-Unis pour un employeur américain, vous pouvez, sous réserve des modalités de toute convention collective applicable, faire verser des cotisations de retraite à la Caisse canadienne.</p>
            <p className="mb-4">Si vous avez travaillé au Canada et aux États-Unis et que des cotisations de retraite ont été versées en votre nom au régime canadien et au régime américain, les deux régimes ont des dispositions qui permettent que le crédit dans un régime soit reconnu comme un crédit dans l'autre aux fins d'admissibilité et d'acquisition des droits.</p>
            <p className="mb-4">Veuillez consulter le résumé du régime pour plus de renseignements ou communiquer avec le Bureau de la Caisse par courriel.</p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/fr#summary-plan"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
              >
                Voir le résumé du régime
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
              <a
                href="mailto:info@mpfcanada.ca"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
              >
                Envoyer un courriel au Bureau de la Caisse
                <i className="ri-mail-line text-sm"></i>
              </a>
            </div>
          </div>
        ),
      },
      {
        question: "Si je travaille pour un orchestre symphonique et suis en congé, les cotisations de retraite sont-elles encore versées pour moi?",
        answer: (
          <div>
            <p className="mb-3">Les cotisations au régime sont versées en fonction des conventions collectives, qui sont des ententes entre la FCM/AFM et les employeurs. Si l'entente en vertu de laquelle vous êtes couvert permet la poursuite des cotisations pendant un congé autorisé, alors les cotisations continueront.</p>
            <p>En vertu de la Loi de l'impôt sur le revenu, il existe des limites quant à la durée pendant laquelle les cotisations peuvent être versées pendant un congé autorisé.</p>
          </div>
        ),
      },
      {
        question: "Pourquoi mon employeur a-t-il déclaré mes cotisations de retraite sur mon T4/T4A? Comment cela m'affecte-t-il?",
        answer: (
          <div>
            <p className="mb-3">La Loi de l'impôt sur le revenu exige que les facteurs d'équivalence, aussi appelés FE, soient déclarés pour toutes les cotisations de retraite versées au régime.</p>
            <p className="mb-3">Chaque année, le montant des cotisations de l'employeur versées en votre nom à la Caisse doit être déclaré sur votre T4/T4A dans la case intitulée « Facteur d'équivalence ». Ce montant est utilisé pour réduire vos droits de cotisation à un REER l'année suivante.</p>
            <p>La Caisse n'est pas votre employeur et ne déclare donc pas les facteurs d'équivalence. En vertu de la Loi de l'impôt sur le revenu, il incombe à l'employeur de déclarer le FE.</p>
          </div>
        ),
      },
      {
        question: "Je reçois une pension mais je travaille encore. Des cotisations de retraite sont-elles encore versées pour moi?",
        answer: (
          <div>
            <p className="mb-3">Oui. Si vous continuez à travailler dans le cadre d'un emploi visé après avoir commencé à recevoir des versements de retraite, des cotisations de retraite continueront d'être versées pour vous.</p>
            <p className="mb-3">Jusqu'à la fin de l'année civile où vous atteignez 71 ans, vous continuerez de recevoir votre retraite du régime et pourriez recevoir une prestation de retraite supplémentaire basée sur les cotisations de l'employeur versées au compte post-retraite.</p>
            <p className="mb-4">Veuillez consulter le résumé du régime pour plus de renseignements sur le compte post-retraite.</p>
            <p className="mb-4">Si vous continuez à travailler dans le cadre d'un emploi visé après le 31 décembre de l'année où vous atteignez 71 ans, la Loi de l'impôt sur le revenu interdit à la Caisse d'accepter des cotisations versées en votre nom. Si votre employeur verse une cotisation en votre nom à la Caisse, la Caisse de retraite des musiciens du Canada remboursera la cotisation à votre employeur.</p>
            <Link
              to="/fr#summary-plan"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              Voir le résumé du régime
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>
        ),
      },
    ],
  },
  {
    id: "life-events",
    title: "Événements de la vie",
    icon: "ri-calendar-event-line",
    faqs: [
      {
        question: "Que dois-je faire lorsque je me prépare à la retraite?",
        answer: (
          <div>
            <p className="mb-3">Voici ce que vous devez faire lorsque vous vous préparez à la retraite :</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Avisez le Bureau de la Caisse de votre retraite au moins trois mois avant la date prévue de votre retraite. Lorsque le Bureau de la Caisse reçoit votre demande, une demande de retraite et les instructions vous seront envoyées par la poste ou par courriel à votre adresse personnelle figurant au dossier.</li>
              <li>Faites une demande de prestation de retraite en remplissant et en soumettant les formulaires nécessaires au Bureau de la Caisse.</li>
              <li>Soumettez une preuve d'âge pour vous-même et votre conjoint, et une preuve d'état matrimonial s'il y a lieu, lorsque vous soumettez vos formulaires.</li>
            </ul>
            <p className="mt-3">Le Bureau de la Caisse accusera réception de votre demande et vous indiquera si des renseignements supplémentaires sont nécessaires.</p>
          </div>
        ),
      },
      {
        question: "Qu'arrive-t-il si je me marie après avoir commencé à recevoir ma prestation de retraite?",
        answer: (
          <div>
            <p className="mb-3">Si vous vous mariez après avoir commencé à recevoir votre prestation de retraite, le régime ne versera une prestation à votre conjoint que si :</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>La forme de paiement que vous avez choisie au moment de la retraite comporte une période de garantie</li>
              <li>Vous décédez avant la fin de la période de garantie</li>
              <li>Votre conjoint est votre bénéficiaire désigné</li>
            </ul>
          </div>
        ),
      },
      {
        question: "Qu'arrive-t-il si je divorce avant ou pendant que je reçois ma prestation de retraite?",
        answer: (
          <div>
            <p className="mb-3">Si vous divorcez avant ou pendant que vous recevez une prestation du régime, la prestation à laquelle votre ancien conjoint pourrait avoir droit dépend de la loi applicable et des documents judiciaires ou ententes liés à votre divorce.</p>
            <p className="mb-3">Assurez-vous de déposer tout document judiciaire ou entente liés aux prestations de retraite et à votre divorce auprès du Bureau de la Caisse dès que possible.</p>
            <p>Si votre ancien conjoint a droit à une partie de votre prestation, la prestation que vous recevez sera ajustée en conséquence.</p>
          </div>
        ),
      },
      {
        question: "Dois-je payer la Caisse pour obtenir des calculs de rupture de mariage si je divorce ou si mon mariage est annulé?",
        answer: (
          <div>
            <p className="mb-3">Oui. La Caisse doit payer un actuaire pour les calculs de rupture de mariage et il ne serait pas équitable que les autres membres du régime assument le coût total de ces calculs.</p>
            <p>Les frais facturés aux membres sont établis par chaque province et sont bien inférieurs aux frais réels engagés par la Caisse.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: "general",
    title: "Général",
    icon: "ri-question-answer-line",
    faqs: [
      {
        question: "Comment puis-je vérifier les renseignements de mon compte?",
        answer: (
          <div>
            <p className="mb-3">Pour voir les cotisations versées par les employeurs à votre compte, visitez le portail membre de la Caisse de retraite des musiciens du Canada.</p>
            <p className="mb-3">Entrez votre nom d'utilisateur et votre mot de passe. Votre nom d'utilisateur est votre adresse courriel au dossier de la Caisse de retraite des musiciens du Canada.</p>
            <p className="mb-3">Ensuite, cliquez sur l'onglet des cotisations pour voir la liste des cotisations de retraite traitées à ce jour en votre nom.</p>
            <p className="mb-3">Si vous vous connectez pour la première fois ou avez oublié votre mot de passe, utilisez la fonction « Mot de passe oublié » pour recevoir un mot de passe temporaire par courriel.</p>
            <p className="mb-4">Si vous avez besoin d'aide, veuillez appeler le Bureau de la Caisse au 1-888-462-6666.</p>
            <a
              href="/member-ui/login/MPF/Member/fr"
              target="_blank"
              rel="nofollow"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              Accéder au portail membre
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        ),
      },
      {
        question: "Comment les cotisations à la Caisse sont-elles investies?",
        answer: (
          <div>
            <p className="mb-3">Les cotisations à la Caisse sont investies dans un portefeuille diversifié d'actifs et sont gérées par des gestionnaires de placements professionnels.</p>
            <p className="mb-3">Les actifs du régime sont détenus en fiducie par un dépositaire indépendant conformément à la législation sur les pensions.</p>
            <p className="mb-4">Consultez le plus récent rapport annuel de la Caisse, disponible sur le site Web de la Caisse de retraite des musiciens du Canada sous l'onglet Ressources, pour plus de renseignements sur les placements de la Caisse.</p>
            <a
              href="/fr/resources?category=annual-reports"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              Voir les rapports annuels
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
        ),
      },
    ],
  },
];

/* ──────────────────────── sub-components ──────────────────────── */

function AccordionItem({ faq, isOpen, onToggle, id }: { faq: FAQItem; isOpen: boolean; onToggle: () => void; id: string }) {
  return (
    <div id={id} className={`border rounded-md overflow-hidden bg-white transition-colors duration-200 ${isOpen ? "border-[#1a8fc1]/30" : "border-gray-200 hover:border-gray-300"}`}>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left cursor-pointer hover:bg-[#f4f8fb] transition-colors duration-200 group ${isOpen ? "bg-[#f4f8fc]" : ""}`}
      >
        <span className={`text-sm pr-2 transition-colors duration-200 ${isOpen ? "font-semibold text-[#0d1f4c]" : "font-medium text-[#0d1f4c] group-hover:text-[#0d1f4c]/80"}`}>
          {faq.question}
        </span>
        <span className={`flex-shrink-0 w-5 h-5 flex items-center justify-center transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
          <i className="ri-arrow-down-s-line text-lg text-[#1a8fc1]"></i>
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          isOpen ? "max-h-[4000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 pb-4 pt-1 text-sm text-gray-600 leading-relaxed border-t border-gray-200">
          {faq.answer}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────── page ──────────────────────── */

export default function FrFAQ() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const getCategoryFromParams = (): string => {
    const cat = searchParams.get("category");
    if (cat && faqCategories.some((c) => c.id === cat)) return cat;
    // Backward compat: check old hash
    if (typeof window !== "undefined" && window.location.hash) {
      const hash = window.location.hash.replace("#", "");
      if (hash && faqCategories.some((c) => c.id === hash)) return hash;
    }
    return "the-fund-and-how-it-works";
  };

  const getQuestionFromParams = (): number | null => {
    const q = searchParams.get("question");
    if (q !== null) {
      const idx = parseInt(q, 10);
      const cat = getCategoryFromParams();
      const category = faqCategories.find((c) => c.id === cat);
      if (category && idx >= 0 && idx < category.faqs.length && !isNaN(idx)) {
        return idx;
      }
    }
    return null;
  };

  const [activeId, setActiveId] = useState(getCategoryFromParams);
  const [openIndex, setOpenIndex] = useState<number | null>(getQuestionFromParams);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const newId = getCategoryFromParams();
    setActiveId(newId);
    const newOpenIndex = getQuestionFromParams();
    setOpenIndex(newOpenIndex);

    if (newOpenIndex !== null) {
      setTimeout(() => {
        const el = document.getElementById(`faq-${newOpenIndex}`);
        if (el) {
          const headerOffset = 80;
          const questionTop = el.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top: questionTop, behavior: "smooth" });
        }
      }, 350);
    }
  }, [searchParams]);

  const handleCategorySelect = useCallback((id: string) => {
    setActiveId(id);
    setOpenIndex(null);
    setSearchParams({ category: id }, { replace: true });
  }, [setSearchParams]);

  const handleToggle = useCallback((idx: number) => {
    const isOpening = openIndex !== idx;
    const newOpenIndex = openIndex === idx ? null : idx;
    setOpenIndex(newOpenIndex);

    if (newOpenIndex !== null) {
      setSearchParams({ category: activeId, question: String(newOpenIndex) }, { replace: true });
    } else {
      setSearchParams({ category: activeId }, { replace: true });
    }

    if (isOpening) {
      setTimeout(() => {
        const el = document.getElementById(`faq-${idx}`);
        if (!el) return;
        const headerOffset = 80;
        const bottomPad = 24;
        const answerEl = el.querySelector(".overflow-hidden") as HTMLElement | null;
        if (!answerEl) return;
        const answerBottom = answerEl.getBoundingClientRect().bottom;
        if (answerBottom <= window.innerHeight - bottomPad) return;
        const questionTop = el.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: questionTop, behavior: "smooth" });
      }, 350);
    }
  }, [openIndex, activeId, setSearchParams]);

  const activeCategory = faqCategories.find((c) => c.id === activeId) ?? faqCategories[0];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Navbar />

      <main className="flex-1">
        <section className="w-full bg-[#0d1f4c] py-10">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-0.5 bg-[#1a8fc1]"></div>
              <span className="text-[#7ec8e3] text-xs font-semibold tracking-widest uppercase">
                {t("faq.pageLabel")}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              {t("faq.pageTitle")}
            </h1>
            <p className="text-base text-white/70 max-w-[560px]">
              {t("faq.pageDesc")}
            </p>
          </div>
        </section>

        <div className="lg:hidden w-full bg-[#f4f8fc] border-b border-gray-100 py-4">
          <div className="max-w-[1200px] mx-auto px-6">
            <label htmlFor="faq-cat-select" className="sr-only">{t("faq.selectCategory")}</label>
            <select
              id="faq-cat-select"
              value={activeId}
              onChange={(e) => handleCategorySelect(e.target.value)}
              className="w-full text-sm font-medium text-[#0d1f4c] bg-white border border-gray-200 rounded-lg px-4 py-3 appearance-none cursor-pointer focus:outline-none focus:border-[#1a8fc1] focus:ring-2 focus:ring-[#1a8fc1]/20"
              style={{
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230d1f4c' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                paddingRight: "40px",
              }}
            >
              {faqCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.title}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="w-full bg-white">
          <div className="max-w-[1200px] mx-auto px-6 flex flex-col lg:flex-row gap-0">
            <aside className="hidden lg:block w-[280px] flex-shrink-0 border-r border-gray-100 py-8 pr-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                {t("faq.categories")}
              </h3>
              <nav className="space-y-0.5">
                {faqCategories.map((cat) => {
                  const isActive = cat.id === activeId;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        isActive
                          ? "bg-[#e8f2f9] text-[#0d1f4c] font-semibold border-l-2 border-[#1a8fc1]"
                          : "text-gray-600 hover:bg-[#f4f8fb] hover:text-[#0d1f4c] font-normal border-l-2 border-transparent"
                      }`}
                    >
                      <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${isActive ? "text-[#1a8fc1]" : "text-gray-400"}`}>
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      <span className="whitespace-nowrap">{cat.title}</span>
                      {isActive && (
                        <span className="ml-auto w-1 h-4 rounded-full bg-[#1a8fc1] flex-shrink-0"></span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </aside>

            <div className="flex-1 min-w-0 py-8 lg:pl-8">
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-0.5 bg-[#1a8fc1]"></div>
                  <h2 className="text-lg font-bold text-[#0d1f4c]">
                    {activeCategory.title}
                  </h2>
                </div>
              </div>

              <div className="bg-[#fafbfd] rounded-lg border border-gray-100 p-5">
                <div className="space-y-2.5">
                {activeCategory.faqs.map((faq, idx) => (
                  <AccordionItem
                    key={idx}
                    faq={faq}
                    isOpen={openIndex === idx}
                    onToggle={() => handleToggle(idx)}
                    id={`faq-${idx}`}
                  />
                ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}